import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Tooltip from '@material-ui/core/Tooltip'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import PromptContext from '../../../../React/Common/Contexts/PromptContext'
import { PaginationInfo } from '../../../../NonReact/Types/Types'
import { AdminFilters, AdminFilterValues } from '../../Molecules/AdminFilters/AdminFilters'
import { SelectFilterOption } from '../../Molecules/AdminFilters/SelectFilter'
import { PageTitle } from '../../Molecules/PageTitle/PageTitle'
import { DataTable, DataTableColumn, DataTableToolbarCustomAction } from '../../Molecules/DataTable/DataTable'
import { IconName } from '../../../../React/Common/Components/Icon'
import { displayError } from '../../../../React/Common/Utils/Messages'
import {
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    Router as RouterLang,
    SidebarMenu as SidebarMenuLanguage,
    UsersListLanguage
} from '../../../../../i18n'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../Hooks/useAdminStatState'
import { useAdminListLegacyServletSwitch } from '../../../Hooks/useAdminListLegacyServletSwitch'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import BulkInsertUsers from '../../../../React/BulkInsertUsers'
import { UserInfo, useUserInfo } from '../../../Hooks/useUserInfo'
import { getUsersList } from '../../../../React/Common/API/ItemsGetters'
import {
    UsersList as UsersListRows,
    UsersListItem,
    UsersListResponse,
    UsersListRow
} from '../../../../NonReact/Types/UsersListTypes'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../../../React/Common/Constants/DefaultValues'
import { DEFAULT_CME_ID } from '../../../../React/Common/Constants/ServerValues'
import AddUserModal from '../../../../React/AddUser/AddUserModal'
import { CopyReportsToWizardModal } from './CopyReportsToWizardModal'
import { adminSignInAsUser, deleteUser } from '../../../../NonReact/common/API/EntityAPIClient'
import { notifyFusionHostOfSignInAsChange } from '../../../../NonReact/common/FusionReauthBridge'
import { isFusionFramed } from '../../../../NonReact/common/FusionThemeBridge'
import { EMPTY_STRING } from '../../../../React/Common/Constants/Separators'
import { AdminListUrlBootstrap, parseAdminListUrlSearchParams } from '../../../Utils/adminListUrlParams'
import { hasResolvedInitialTelSysPartitionListFilters } from '../../../Utils/adminListFilterReadiness'
import { buildAdminListRowKey } from '../../../Utils/adminListRowKeys'
import { APIElements } from '../../../../NonReact/constants/ApiConstants'
import { redirectToServletWithBranding, ServletCmds } from '../../../../NonReact/constants/ServletCommands'
import { useBranding } from '../../../../React/Common/Hooks/useBranding'
import { Branding } from '../../../../React/Common/API/BrandingAPI'
import { LanguageUtils } from '../../../../React/Common/Utils/LanguageUtils'

const USER_TYPE_ALL: string = String(ALL_VALUE)

const SERVLET_CMD_PREFIX: string = 'CMD_'

/**
 * Normalises the servlet command returned after sign-in-as (ensures a `CMD_` prefix when the API omits it).
 *
 * @param redirectRaw - Optional redirect command from the API.
 * @returns Servlet command suitable for {@link redirectToServletWithBranding}.
 */
const normaliseSignInAsRedirectCommand = (redirectRaw: Definable<string>): string => {
    if (!isStringNotEmpty(redirectRaw)) return ServletCmds.MAIN_HOME

    if (redirectRaw.startsWith(SERVLET_CMD_PREFIX)) return redirectRaw

    return `${SERVLET_CMD_PREFIX}${redirectRaw}`
}

/**
 * True when the filter value is a concrete TelSys or partition selection (excludes [All] / Any placeholders).
 *
 * @param value - Raw telephony or partition option value from admin filters.
 * @returns Whether the value should be sent to the user list API as a restricting parameter.
 */
const isConcreteTelSysOrPartitionFilterValue = (value: unknown): boolean => {
    if (IsVarEmpty(value)) return false

    if (value === ALL_VALUE) return false

    if (value === ANY_VALUE_STRING_NUMERIC) return false

    return true
}

/**
 * Parses a lock-out field returned by the admin user list API into a stable display string.
 *
 * @param lockOut - Raw lock-out value from the API.
 * @param yesLabel - Localised "yes" label.
 * @param noLabel - Localised "no" label.
 * @returns Localised lock-out display text.
 */
const formatLockOutDisplay = (lockOut: Definable<boolean | string>, yesLabel: string, noLabel: string): string => {
    if (lockOut === true) return yesLabel

    if (lockOut === false) return noLabel

    if (isStringNotEmpty(lockOut) && String(lockOut).toLowerCase() === 'true') return yesLabel

    return noLabel
}

/**
 * Removes one surrounding pair of square brackets from backend list values while leaving inner content untouched.
 *
 * @param value - Raw backend display value.
 * @returns Value without one outer `[]` pair when present.
 */
const stripSurroundingSquareBrackets = (value: Definable<string>): string => {
    if (!isStringNotEmpty(value)) return EMPTY_STRING

    if (value.startsWith('[') && value.endsWith(']')) {
        return value.substring(1, value.length - 1)
    }

    return value
}

/**
 * Routed Application Users list view.
 *
 * @returns Application Users list page content.
 */
export const UsersList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(RouterLang.NAME)
    language.loadBundle(UsersListLanguage.NAME)
    language.loadBundle(SidebarMenuLanguage.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const [usersList, setUsersList]: ReactState<Nullable<UsersListRows>> = useState<Nullable<UsersListRows>>()
    const {
        loading,
        setLoading,
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()
    const [userInfo]: UserInfo = useUserInfo()
    const { displayPromptPromise } = useContext(PromptContext)
    const branding: Nullable<Branding> = useBranding()
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [isUserModalOpen, setUserModalOpen]: ReactState<boolean> = useState<boolean>(false)
    const [selectedUsername, setSelectedUsername]: ReactState<Definable<string>> = useState<Definable<string>>()
    const [userModalViewOnly, setUserModalViewOnly]: ReactState<boolean> = useState<boolean>(false)
    const [copyReportsSourceUser, setCopyReportsSourceUser]: ReactState<Definable<UsersListRow>> =
        useState<Definable<UsersListRow>>()
    const [isBulkInsertOpen, setBulkInsertOpen]: ReactState<boolean> = useState<boolean>(false)

    const canSignInAs: boolean = useMemo(
        (): boolean => userInfo?.Permissions?.Admin?.includes(AdminBackendPermissionNames.SIGN_IN_AS) ?? false,
        [userInfo]
    )

    const [searchParams] = useSearchParams()
    const urlBootstrap: OnlyNullable<AdminListUrlBootstrap> = useMemo((): OnlyNullable<AdminListUrlBootstrap> => {
        const parsed: AdminListUrlBootstrap = parseAdminListUrlSearchParams(searchParams)

        return Object.keys(parsed).length > 0 ? parsed : null
    }, [searchParams])
    const [hasResolvedInitialUrlBootstrapLoad, setHasResolvedInitialUrlBootstrapLoad]: ReactState<boolean> =
        useState<boolean>(() => IsVarEmpty(urlBootstrap))
    const [hasResolvedInitialFiltersLoad, setHasResolvedInitialFiltersLoad]: ReactState<boolean> =
        useState<boolean>(false)
    const [hasReceivedInitialFilters, setHasReceivedInitialFilters]: ReactState<boolean> = useState<boolean>(false)
    const isBootstrappingFromUrl: boolean = isVarNotEmpty(urlBootstrap) && IsVarEmpty(filters)
    const isWaitingForInitialUrlBootstrapLoad: boolean =
        isVarNotEmpty(urlBootstrap) && !hasResolvedInitialUrlBootstrapLoad
    const isWaitingForInitialFiltersLoad: boolean = !hasResolvedInitialFiltersLoad

    const cmeFilterValue: Definable<string | number> = filters?.cme?.value
    const listCmeId: Nullable<string> = IsVarEmpty(cmeFilterValue)
        ? userInfo?.Scope?.CMEnvironmentID ?? null
        : String(cmeFilterValue)
    const listIncludeRecursive: boolean = filters?.includeCmeChildren ?? true
    const listSearch: string = filters?.search ?? EMPTY_STRING
    const telSysFilterValue: Definable<string | number> = filters?.telephonyServer?.value
    const partitionFilterValue: Definable<string | number> = filters?.partition?.value
    const listTelSysId: Definable<string | number> = isConcreteTelSysOrPartitionFilterValue(telSysFilterValue)
        ? telSysFilterValue
        : ALL_VALUE
    const listPartIDInTelSys: Definable<string | number> =
        isConcreteTelSysOrPartitionFilterValue(telSysFilterValue) &&
        isConcreteTelSysOrPartitionFilterValue(partitionFilterValue)
            ? partitionFilterValue
            : ALL_VALUE
    const listNoTelephonyServer: boolean = filters?.noTelephonyServer ?? false

    useAdminListLegacyServletSwitch(ServletCmds.ADMIN_USERS, filters, userInfo?.Scope?.CMEnvironmentID)

    const userTypeOptionValue: Definable<string | number> = filters?.userType?.value
    const listUserTypeFilter: Definable<number> = useMemo((): Definable<number> => {
        if (IsVarEmpty(userTypeOptionValue) || userTypeOptionValue === EMPTY_STRING) {
            return undefined
        }
        const parsed: number = Number.parseInt(String(userTypeOptionValue), 10)
        if (Number.isNaN(parsed) || parsed === ALL_VALUE) {
            return undefined
        }
        return parsed
    }, [userTypeOptionValue])

    const filterCMEnvironmentIDForModal: string = String(
        filters?.cme?.value ?? userInfo?.Scope?.CMEnvironmentID ?? DEFAULT_CME_ID
    )

    const modalFilterTelSysID: Definable<number> = isConcreteTelSysOrPartitionFilterValue(telSysFilterValue)
        ? Number(telSysFilterValue)
        : undefined

    const modalFilterPartitionID: Definable<number> =
        isConcreteTelSysOrPartitionFilterValue(telSysFilterValue) &&
        isConcreteTelSysOrPartitionFilterValue(partitionFilterValue)
            ? Number(partitionFilterValue)
            : undefined

    const bulkInsertServletPath: string = useMemo((): string => {
        const trimmedMapping: string = (branding?.URLMapping ?? EMPTY_STRING).replace(/\//g, EMPTY_STRING)

        return isStringNotEmpty(trimmedMapping) ? trimmedMapping : APIElements.AKIXI_2019
    }, [branding?.URLMapping])

    /**
     * Fetches application users for the current filter state.
     *
     * @returns No return value.
     */
    const getApplicationUsers = useCallback((): void => {
        if (!hasReceivedInitialFilters) return

        if (isBootstrappingFromUrl) return

        if (IsVarEmpty(listCmeId)) {
            setHasResolvedInitialUrlBootstrapLoad(true)
            setHasResolvedInitialFiltersLoad(true)
            return
        }

        setLoading((currentLoading: number): number => currentLoading + 1)

        if (listNoTelephonyServer) {
            if (isMounted.current) {
                setUsersList([])
                setTotalCount(0)
            }
            setHasResolvedInitialUrlBootstrapLoad(true)
            setHasResolvedInitialFiltersLoad(true)
            setLoading((currentLoading: number): number => currentLoading - 1)
            return
        }

        const [currentPage, rowsPerPage]: PaginationInfo = pagination
        const pageStartIndex: number = currentPage * rowsPerPage
        const searchQuery: Definable<string> = listSearch === EMPTY_STRING ? undefined : listSearch

        getUsersList(
            listTelSysId ?? ALL_VALUE,
            listPartIDInTelSys ?? ALL_VALUE,
            listCmeId,
            listIncludeRecursive,
            pageStartIndex,
            rowsPerPage,
            sort,
            searchQuery,
            listUserTypeFilter
        )
            .then((response: Nullable<UsersListResponse>): void => {
                if (!isMounted.current) return

                const rows: UsersListRows =
                    response?.Data?.Items?.map(
                        (item: UsersListItem, index: number): UsersListRow => ({
                            key: buildAdminListRowKey(
                                [
                                    listCmeId,
                                    listTelSysId,
                                    listPartIDInTelSys,
                                    stripSurroundingSquareBrackets(item.Username),
                                    item.FullName
                                ],
                                pageStartIndex,
                                index
                            ),
                            Description: item.FullName ?? EMPTY_STRING,
                            Username: stripSurroundingSquareBrackets(item.Username),
                            FullName: item.FullName ?? EMPTY_STRING,
                            LockOutDisplay: formatLockOutDisplay(
                                item.LockOut,
                                language.get(UsersListLanguage.UsersList_LockOut_Yes),
                                language.get(UsersListLanguage.UsersList_LockOut_No)
                            ),
                            ListActivity: stripSurroundingSquareBrackets(item.ListActivity),
                            ListLastLogon: stripSurroundingSquareBrackets(item.ListLastLogon),
                            ListPermissionsSummary: stripSurroundingSquareBrackets(item.ListPermissionsSummary),
                            ListTelSysDescription: stripSurroundingSquareBrackets(item.ListTelSysDescription)
                        })
                    ) ?? []

                setUsersList(rows)
                setTotalCount(Number(response?.Data?.Total) || 0)
            })
            .catch(displayError)
            .finally((): void => {
                setHasResolvedInitialUrlBootstrapLoad(true)
                setHasResolvedInitialFiltersLoad(true)

                if (!isMounted.current) return

                setLoading((currentLoading: number): number => currentLoading - 1)
            })
    }, [
        hasReceivedInitialFilters,
        isBootstrappingFromUrl,
        listCmeId,
        listIncludeRecursive,
        listNoTelephonyServer,
        listPartIDInTelSys,
        listSearch,
        listTelSysId,
        listUserTypeFilter,
        pagination,
        sort,
        userInfo?.Scope?.CMEnvironmentID
    ])

    const refreshUsersList = useCallback((): void => {
        getApplicationUsers()
    }, [getApplicationUsers])

    const handleToolbarSignInAsClick = useCallback(
        ([selectedItem]: UsersListRow[]): void => {
            displayPromptPromise(
                LanguageUtils.formatLanguageEntry(
                    language.getInterpolated(UsersListLanguage.UsersList_SignInAs_Confirm, selectedItem.Username)
                )
            )
                .then((): Promise<void> => {
                    setLoading((currentLoading: number): number => currentLoading + 1)

                    return adminSignInAsUser(selectedItem.Username)
                        .then(async (response): Promise<void> => {
                            // Embedded in the new UI: skip the legacy in-iframe redirect entirely.
                            // The server session is now impersonating; hand it up to the host, which
                            // adopts the new user's token and routes to the realtime dashboard as
                            // that user (CXUI-402).
                            if (isFusionFramed()) {
                                await notifyFusionHostOfSignInAsChange()

                                return
                            }

                            const command: string = normaliseSignInAsRedirectCommand(response?.Data?.RedirectThePageTo)

                            redirectToServletWithBranding(command, branding?.URLMapping ?? APIElements.AKIXI_2019)
                        })
                        .catch((error: Error): void => {
                            displayError(error)
                        })
                        .finally((): void => {
                            if (!isMounted.current) return

                            setLoading((currentLoading: number): number => currentLoading - 1)
                        })
                })
                .catch((): void => {
                    // User cancelled the confirmation prompt.
                })
        },
        [branding?.URLMapping, displayPromptPromise, isMounted, language]
    )

    const toolbarCustomActions: DataTableToolbarCustomAction<UsersListRow>[] =
        useMemo((): DataTableToolbarCustomAction<UsersListRow>[] => {
            const actions: DataTableToolbarCustomAction<UsersListRow>[] = [
                {
                    label: language.get(UsersListLanguage.UsersList_CopyReports_Button),
                    icon: IconName.COPY,
                    isVisible: (count: number): boolean => count === 1,
                    requiresUpdatePermission: true,
                    onClick: ([selectedItem]: UsersListRow[]): void => {
                        setCopyReportsSourceUser(selectedItem)
                    }
                }
            ]

            if (canSignInAs) {
                actions.push({
                    label: language.get(UsersListLanguage.UsersList_SignInAs_Button),
                    icon: IconName.PERSON,
                    isVisible: (count: number): boolean => count === 1,
                    requiresUpdatePermission: false,
                    onClick: handleToolbarSignInAsClick
                })
            }

            return actions
        }, [canSignInAs, handleToolbarSignInAsClick, language])

    const userTypeOptions: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            { value: USER_TYPE_ALL, label: language.get(UsersListLanguage.UsersList_UserType_All) },
            { value: '1', label: language.get(UsersListLanguage.UsersList_UserType_ServiceWide) },
            { value: '2', label: language.get(UsersListLanguage.UsersList_UserType_SystemWide) },
            { value: '10', label: language.get(UsersListLanguage.UsersList_UserType_Detached) },
            { value: '20', label: language.get(UsersListLanguage.UsersList_UserType_AdminInclusive) },
            { value: '21', label: language.get(UsersListLanguage.UsersList_UserType_AdminExclusive) },
            { value: '30', label: language.get(UsersListLanguage.UsersList_UserType_ReportingHistoricOnly) },
            { value: '31', label: language.get(UsersListLanguage.UsersList_UserType_ReportingBLF) },
            { value: '33', label: language.get(UsersListLanguage.UsersList_UserType_ReportingStd) },
            { value: '34', label: language.get(UsersListLanguage.UsersList_UserType_ReportingACD) },
            { value: '36', label: language.get(UsersListLanguage.UsersList_UserType_ReportingMobileAppAdvanced) },
            { value: '41', label: language.get(UsersListLanguage.UsersList_UserType_SignedInNow) },
            { value: '42', label: language.get(UsersListLanguage.UsersList_UserType_SignedInToday) },
            { value: '43', label: language.get(UsersListLanguage.UsersList_UserType_SignedInThisWeek) },
            { value: '45', label: language.get(UsersListLanguage.UsersList_UserType_SignedInThisMonth) },
            { value: '50', label: language.get(UsersListLanguage.UsersList_UserType_SignedInNever) },
            { value: '60', label: language.get(UsersListLanguage.UsersList_UserType_ActivityToday) },
            { value: '61', label: language.get(UsersListLanguage.UsersList_UserType_ActivityThisWeek) },
            { value: '63', label: language.get(UsersListLanguage.UsersList_UserType_ActivityThisMonth) },
            { value: '70', label: language.get(UsersListLanguage.UsersList_UserType_ActivityNone) }
        ],
        [language]
    )

    const columns: DataTableColumn<UsersListRow, keyof UsersListRow>[] = useMemo((): DataTableColumn<
        UsersListRow,
        keyof UsersListRow
    >[] => {
        const lastLogonTooltipLabel: string = language.get(UsersListLanguage.UsersList_Tooltip_LastLogon_Label)
        const lastLogonNoneLabel: string = language.get(UsersListLanguage.UsersList_LastLogon_None)

        const buildLastLogonTooltipTitle = (row: UsersListRow): string => {
            const lastLogonValue: string = isStringNotEmpty(row.ListLastLogon) ? row.ListLastLogon : lastLogonNoneLabel

            return `${lastLogonTooltipLabel}: ${lastLogonValue}`
        }

        return [
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_Username),
                field: 'Username',
                key: 'users-list-username',
                sortable: true,
                align: 'left'
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_FullName),
                field: 'FullName',
                key: 'users-list-fullname',
                sortable: true,
                align: 'left',
                hideBelow: 'sm'
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_LockOut),
                field: 'LockOutDisplay',
                key: 'users-list-lockout',
                sortable: true,
                serverSortField: 'LockOut'
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_ActivityLastLogon),
                field: 'ListActivity',
                key: 'users-list-activity-last-logon',
                sortable: true,
                renderCell: (row: UsersListRow): ReactElement => (
                    <Tooltip
                        classes={{ tooltip: 'users-list-last-logon-tooltip' }}
                        title={buildLastLogonTooltipTitle(row)}
                        placement='top'
                        enterDelay={400}
                    >
                        <span className='users-list-activity-last-logon-cell'>{row.ListActivity}</span>
                    </Tooltip>
                )
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_Permissions),
                field: 'ListPermissionsSummary',
                key: 'users-list-permissions',
                sortable: true,
                hideBelow: 'md'
            },
            {
                headerName: language.get(UsersListLanguage.UsersList_Column_TelSys),
                field: 'ListTelSysDescription',
                key: 'users-list-telsys',
                sortable: true,
                hideBelow: 'sm'
            }
        ]
    }, [language])

    useEffect((): void => {
        setHasResolvedInitialUrlBootstrapLoad(IsVarEmpty(urlBootstrap))
    }, [urlBootstrap])

    useEffect(getApplicationUsers, [getApplicationUsers])

    return (
        <div className='users-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_users}></PageTitle>
            <div className='users-list-table-container admin-table-list-container'>
                <AdminFilters
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        partitionLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_PARTITION_LABEL
                        ),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL),
                        userTypeLabel: language.get(UsersListLanguage.UsersList_Filter_UserType_Label)
                    }}
                    urlBootstrap={urlBootstrap}
                    onChange={(values: AdminFilterValues): void => {
                        setFilters(values)

                        if (hasResolvedInitialTelSysPartitionListFilters(values)) {
                            setHasReceivedInitialFilters(true)
                        }
                    }}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: true,
                        crmType: false,
                        scope: false,
                        codeType: false,
                        userType: true,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    onRefresh={getApplicationUsers}
                    userTypeOptions={userTypeOptions}
                />
                <DataTable<UsersListRow, keyof UsersListRow>
                    permissionName={AdminBackendPermissionNames.USER}
                    localeText={{
                        addButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Add),
                        bulkAddButton: language.get(UsersListLanguage.UsersList_BulkAdd_Button),
                        viewButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_View),
                        changeButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Change),
                        deleteButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Delete),
                        selectionActive: (count: number): string =>
                            language.getInterpolated(GenericAdminListLanguage.GenericAdminList_Footer_Selected, count),
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    columns={columns}
                    data={usersList ?? []}
                    defaultRowsPerPageOptions={[10, 25, 50, 100]}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    isLoading={loading > 0 || isWaitingForInitialUrlBootstrapLoad || isWaitingForInitialFiltersLoad}
                    paginationServer
                    sortServer
                    isSelectable
                    colIndexToTriggerClick={0}
                    onToolbarAddClick={(): void => {
                        setSelectedUsername(undefined)
                        setUserModalViewOnly(false)
                        setUserModalOpen(true)
                    }}
                    onToolbarBulkAddClick={(): void => {
                        setBulkInsertOpen(true)
                    }}
                    onToolbarChangeClick={([selectedItem]: UsersListRow[]): void => {
                        setSelectedUsername(selectedItem.Username)
                        setUserModalViewOnly(false)
                        setUserModalOpen(true)
                    }}
                    onToolbarViewClick={([selectedItem]: UsersListRow[]): void => {
                        setSelectedUsername(selectedItem.Username)
                        setUserModalViewOnly(true)
                        setUserModalOpen(true)
                    }}
                    toolbarCustomActions={toolbarCustomActions}
                    onToolbarDeleteClick={(selectedItems: UsersListRow[]): void => {
                        setLoading((currentLoading: number): number => currentLoading + 1)
                        Promise.all(
                            selectedItems.map((row: UsersListRow): Promise<unknown> => deleteUser(row.Username))
                        )
                            .then((): void => {
                                getApplicationUsers()
                            })
                            .catch((error: Error): void => {
                                displayError(error)
                            })
                            .finally((): void => {
                                if (isMounted.current)
                                    setLoading((currentLoading: number): number => currentLoading - 1)
                            })
                    }}
                    onSortChange={setSort}
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                        setPagination([currentPage, newRowsPerPage])
                    }
                    onPageChange={(newPage: number, rowsPerPage: number): void => setPagination([newPage, rowsPerPage])}
                />
                {isVarNotEmpty(copyReportsSourceUser) ? (
                    <CopyReportsToWizardModal
                        sourceUser={copyReportsSourceUser}
                        onCompleted={refreshUsersList}
                        onExited={(): void => {
                            setCopyReportsSourceUser(undefined)
                        }}
                    />
                ) : (
                    <></>
                )}
                {isBulkInsertOpen ? (
                    <BulkInsertUsers
                        servletPath={bulkInsertServletPath}
                        refreshAction={refreshUsersList}
                        isRouterMode
                        onWizardExited={(): void => {
                            setBulkInsertOpen(false)
                        }}
                    />
                ) : (
                    <></>
                )}
                {isUserModalOpen ? (
                    <AddUserModal
                        key={`${selectedUsername ?? 'new'}-${
                            userModalViewOnly ? 'v' : 'e'
                        }-${filterCMEnvironmentIDForModal}-${modalFilterTelSysID ?? 'any'}-${
                            modalFilterPartitionID ?? 'any'
                        }-${listIncludeRecursive ? 'cmeR' : 'cmeD'}`}
                        filterCMEID={filterCMEnvironmentIDForModal}
                        filterTelSysID={modalFilterTelSysID}
                        initialFilters={{
                            cmeID: filterCMEnvironmentIDForModal,
                            telSysID: modalFilterTelSysID,
                            partitionID: modalFilterPartitionID,
                            includeCmeChildren: listIncludeRecursive
                        }}
                        refreshAction={refreshUsersList}
                        selectedUser={selectedUsername}
                        viewOnly={userModalViewOnly}
                        onModalExited={(): void => {
                            setUserModalOpen(false)
                            setSelectedUsername(undefined)
                        }}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
